import { useEffect, useState } from "react";
import axios from "axios";
import { MessageCircle } from "lucide-react";
import { API } from "./constants";

export const WhatsAppCard = () => {
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [qrAge, setQrAge] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const poll = () => {
    axios
      .get(`${API}/whatsapp/status`, { withCredentials: true })
      .then((res) => {
        setStatus(res.data);
        if (!res.data.connected && !res.data.error) {
          axios
            .get(`${API}/whatsapp/qr`, { withCredentials: true })
            .then((r) => {
              setQr(r.data.qr);
              setQrAge(typeof r.data.age === "number" ? r.data.age : 0);
            })
            .catch(() => {});
        } else {
          setQr(null);
        }
      })
      .catch(() => setStatus({ connected: false, error: "unreachable" }));
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, []);

  const refreshQr = async () => {
    setRefreshing(true);
    setQr(null);
    try {
      await axios.post(`${API}/whatsapp/reconnect`, {}, { withCredentials: true });
    } catch {
    }
    setTimeout(() => {
      setRefreshing(false);
      poll();
    }, 5000);
  };

  return (
    <div className="mt-6 rounded-xl border border-border bg-white p-6 shadow-soft" data-testid="whatsapp-card">
      <div className="flex items-center gap-2">
        <MessageCircle size={16} className="text-amberdark" />
        <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">WhatsApp AI Auto-Reply</h2>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        When someone messages +91 80504 44041 on WhatsApp, the AI answers basic
        questions instantly and tells them a team member will connect shortly.
      </p>

      <div className="mt-5 flex items-center gap-3" data-testid="whatsapp-status">
        {status === null ? (
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-gray-400">Checking...</span>
        ) : status.error ? (
          <>
            <span className="h-2.5 w-2.5 rounded-full bg-amber" />
            <span className="text-sm font-bold text-amberdark">Not available on the live website yet</span>
          </>
        ) : status.connected ? (
          <>
            <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
            <span className="text-sm font-bold text-green-700">Connected — AI is answering messages</span>
          </>
        ) : (
          <>
            <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
            <span className="text-sm font-bold text-red-600">Not connected — scan the QR code below</span>
          </>
        )}
      </div>

      {status?.cloud_api && (
        <div className="mt-3 flex items-center gap-3" data-testid="whatsapp-cloud-status">
          <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
          <span className="text-sm font-bold text-green-700">Official WhatsApp Business API: active on the live site</span>
        </div>
      )}

      {status?.error && (
        <div className="mt-5 rounded-lg border border-amber/50 bg-[#FFFBF2] p-5" data-testid="whatsapp-unavailable">
          <p className="text-xs leading-relaxed text-gray-600">
            The QR-based bot runs in our preview system only — it cannot run on the
            live website, so no QR code will appear here. The live site gets WhatsApp
            AI through the <span className="font-bold text-navy">official WhatsApp Business API</span> —
            no QR code, no re-scanning, works even after redeploys. It is being set up
            right now — once active, this card shows it here automatically.
          </p>
        </div>
      )}

      {status && !status.connected && !status.error && (
        <div className="mt-5 rounded-lg border border-amber/50 bg-[#FFFBF2] p-5" data-testid="whatsapp-qr-block">
          {qr ? (
            <>
              {qrAge > 45 && (
                <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-xs font-bold text-red-600" data-testid="whatsapp-qr-stale">
                  This QR may have expired — tap "Get fresh QR" below, then scan immediately.
                </p>
              )}
              <img src={qr} alt="WhatsApp QR code" className="h-56 w-56 rounded-md border border-border bg-white" data-testid="whatsapp-qr-image" />
            </>
          ) : (
            <p className="text-sm font-semibold text-gray-600">{refreshing ? "Generating a fresh QR..." : "QR will appear here — tap the button below."}</p>
          )}
          <button
            onClick={refreshQr}
            data-testid="whatsapp-refresh-qr-btn"
            disabled={refreshing}
            className="mt-4 rounded-md bg-navy px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-amber hover:text-navy disabled:opacity-50"
          >
            {refreshing ? "Refreshing..." : "Get fresh QR"}
          </button>
          <p className="mt-3 text-xs leading-relaxed text-gray-600">
            On the phone with number <span className="font-bold text-navy">+91 80504 44041</span>:
            open WhatsApp → Settings → Linked Devices → Link a Device → scan the code.
            <span className="font-bold text-navy"> QR codes expire in about 60 seconds</span> — scan
            immediately after it appears. Do this once; the AI starts replying right after.
          </p>
        </div>
      )}
    </div>
  );
};
